// src/components/Board/ChatPanel.tsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import MessageItem from './MessageItem';

// Icons & Components
import { ReactComponent as ArrowWhiteIcon } from '../../assets/icons/arrow-white.svg';
import { ReactComponent as CopyIcon } from '../../assets/icons/copy-icon.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as ImageIcon } from '../../assets/icons/image-icon.svg';
import { ReactComponent as DatabaseIcon } from '../../assets/icons/database-icon.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';
import { ReactComponent as InfoCircleIcon } from '../../assets/icons/info-circle-icon.svg';
import { ReactComponent as PencilIcon } from '../../assets/icons/pencil.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/icons/chevron-down.svg';
import { ReactComponent as AibexColorSideLogo } from '../../assets/icons/aibex-side-logo-color.svg';
import { ReactComponent as IdeaIcon } from '../../assets/icons/icon-idea.svg';

import styles from '../../assets/styles/components/ChatPanel.module.scss';

// Types
import type { Message } from '../../types/chat';
import type { ChatListItem } from '../../hooks/useChatList';

interface ChatPanelProps {
  messages: Message[];
  input: string;
  setInput: (value: string) => void;
  onSend: (text: string, options?: { aiSuggestionMode?: boolean }) => void;
  isGenerating: boolean;
  isSuggestionMode: boolean;
  onSuggestionModeChange: (enabled: boolean) => void;

  // Chat Room Management
  chatId: string | null;
  chatList: ChatListItem[];
  onDeleteChat: (id: string) => Promise<void>;
  onRenameChat: (id: string, newTitle: string) => Promise<void>;

  // Follow-up States
  followups: { q1: string | null; q2: string | null; q3: string | null };
  followupsVisible: boolean;
  followupsLeaving: boolean;
  onFollowupClick: (text: string) => void;

  onShowRef: (msg: Message) => void;

  // 日付選択パーツ用Props
  selectedPeriod: string;
  setSelectedPeriod: (val: string) => void;
  customStart: string;
  setCustomStart: (val: string) => void;
  customEnd: string;
  setCustomEnd: (val: string) => void;
}

const normalizeMarkdownText = (text?: string) => (text || '').replace(/\\n/g, '\n');

// Message Item component

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  input,
  setInput,
  onSend,
  isGenerating,
  isSuggestionMode,
  onSuggestionModeChange,
  chatId,
  chatList,
  onDeleteChat,
  onRenameChat,
  followups,
  followupsVisible,
  followupsLeaving,
  onFollowupClick,
  onShowRef,
  selectedPeriod,
  setSelectedPeriod,
  customStart,
  setCustomStart,
  customEnd,
  setCustomEnd,
}) => {
  const navigate = useNavigate();
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const plusMenuRef = useRef<HTMLDivElement>(null);

  // Chat title & Popover States
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isRoomPopoverOpen, setIsRoomPopoverOpen] = useState(false);
  const roomPopoverRef = useRef<HTMLDivElement>(null);

  // Auto Scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Click outside plus menu & room popover close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isPlusMenuOpen &&
        plusMenuRef.current &&
        !plusMenuRef.current.contains(e.target as Node)
      ) {
        setIsPlusMenuOpen(false);
      }
      if (
        isRoomPopoverOpen &&
        roomPopoverRef.current &&
        !roomPopoverRef.current.contains(e.target as Node)
      ) {
        setIsRoomPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPlusMenuOpen, isRoomPopoverOpen]);

  // Determine Current Chat Room Title
  const currentChatTitle = useMemo(() => {
    if (!chatId) return '新しいチャット';
    const currentItem = chatList.find((item) => item.chat_id === chatId);
    return currentItem ? currentItem.title : '新しいチャット';
  }, [chatId, chatList]);

  // Start Title Edit
  const handleStartEdit = () => {
    if (!chatId) return; // 新規チャット時は編集不可
    setEditedTitle(currentChatTitle);
    setIsEditingTitle(true);
  };

  // Submit Title Edit
  const handleSubmitEdit = async () => {
    if (!chatId || !editedTitle.trim() || editedTitle === currentChatTitle) {
      setIsEditingTitle(false);
      return;
    }
    await onRenameChat(chatId, editedTitle);
    setIsEditingTitle(false);
  };

  const handleKeyDownTitle = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmitEdit();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (!input.trim() || isGenerating) return;
    onSend(input, { aiSuggestionMode: isSuggestionMode });
    if (textareaRef.current) {
      textareaRef.current.style.height = '36px';
    }
  };

  const handleInputResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '36px';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  const isFirstView = messages.length === 0;

  const quickActions = [
    'CPAを改善するための効果的な広告配信設定は？',
    'ターゲットオーディエンスの予算配分について診断してほしい',
    '現在の入札戦略における改善ポイントを教えてください',
    'AI提案の作成手順について説明してほしい',
  ];

  return (
    <div className={styles.chatPanel}>
      <div className={styles.chatHeader}>
        {/* 左: チャットタイトル (インライン編集可能) + ルーム変更ポップオーバー */}
        <div className={styles.chatHeaderLeft}>
          {/* 先頭: チャットルーム変更 (ドロップダウンポップオーバー) - 下矢印アイコン */}
          <div className={styles.roomSelectorWrap} ref={roomPopoverRef}>
            <button
              type="button"
              className={`${styles.headerIconBtn} ${isRoomPopoverOpen ? styles.open : ''}`}
              onClick={() => setIsRoomPopoverOpen(!isRoomPopoverOpen)}
              title="チャット履歴一覧"
            >
              <ChevronDownIcon />
            </button>

            {isRoomPopoverOpen && (
              <div className={styles.roomPopover}>
                <ul className={styles.roomList}>
                  <li
                    className={styles.newChatListItem}
                    onClick={() => {
                      navigate('/board');
                      setIsRoomPopoverOpen(false);
                    }}
                  >
                    <span>＋ 新しいチャットを始める</span>
                  </li>
                  <div className={styles.popoverDivider} />

                  {(chatList || []).length === 0 ? (
                    <li className={styles.emptyText}>履歴はありません</li>
                  ) : (
                    (chatList || []).map((item) => {
                      const isActive = item.chat_id === chatId;
                      return (
                        <li
                          key={item.chat_id}
                          className={`${styles.roomItem} ${isActive ? styles.active : ''}`}
                          onClick={() => {
                            navigate(`/board/${item.chat_id}`);
                            setIsRoomPopoverOpen(false);
                          }}
                        >
                          <span className={styles.roomTitle}>{item.title}</span>
                          <button
                            type="button"
                            className={styles.roomDeleteBtn}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm(`「${item.title}」を削除してもよろしいですか？`)) {
                                await onDeleteChat(item.chat_id);
                              }
                            }}
                            title="削除"
                          >
                            <TrashIcon />
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* 中央: チャットタイトル (インライン編集可能) */}
          {isEditingTitle ? (
            <input
              type="text"
              className={styles.chatTitleEditInput}
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleSubmitEdit}
              onKeyDown={handleKeyDownTitle}
              autoFocus
            />
          ) : (
            <span className={styles.chatTitle} onDoubleClick={handleStartEdit}>
              {currentChatTitle}
            </span>
          )}

          {/* 末尾: タイトル編集アイコン */}
          {chatId && !isEditingTitle && (
            <button
              type="button"
              className={styles.headerIconBtn}
              onClick={handleStartEdit}
              title="チャット名を編集"
            >
              <PencilIcon />
            </button>
          )}
        </div>

        {/* 右: 日付範囲選択コントロール */}
        <div className={styles.headerRightActions}>
          <div className={styles.periodSelector}>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className={styles.periodSelect}
            >
              <option value="last_30_days">直近30日</option>
              <option value="this_month">今月</option>
              <option value="last_month">先月</option>
              <option value="this_week">今週</option>
              <option value="custom">カスタム</option>
            </select>
          </div>

          {selectedPeriod === 'custom' && (
            <div className={styles.customDateRange}>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className={styles.dateInput}
              />
              <span className={styles.separator}>-</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className={styles.dateInput}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.messageList}>
        {isFirstView ? (
          <div className={styles.firstViewWrapper}>
            <div className={styles.icon}>
              <AibexColorSideLogo className={styles.firstViewLogo} />
            </div>
            <h2 className={styles.title}>Aibexへようこそ</h2>
            <p className={styles.subtitle}>
              広告運用やwebサイトの成果について何でも聞いてください。
              <br />
              または以下のアクションを選択して質問を開始してください。
            </p>
            <div className={styles.suggestedQuestions}>
              {quickActions.map((q, idx) => (
                <div key={idx} className={styles.questionCard} onClick={() => onSend(q)}>
                  <span className={styles.questionText}>{q}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => (
              <MessageItem
                key={index}
                msg={msg}
                onShowRef={onShowRef}
                hideIcon={index > 0 && messages[index - 1].sender === msg.sender}
                isConsecutive={index > 0 && messages[index - 1].sender === msg.sender}
              />
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* チャット入力フォームUI */}
      <div className={styles.inputArea}>
        <div
          className={`${styles.followupWrap} ${
            followupsVisible ? styles.visible : ''
          } ${followupsLeaving ? styles.leaving : ''}`}
        >
          {followups.q1 && (
            <button
              type="button"
              className={styles.followupChip}
              onClick={() => onFollowupClick(followups.q1!)}
            >
              {followups.q1}
            </button>
          )}
          {followups.q2 && (
            <button
              type="button"
              className={styles.followupChip}
              onClick={() => onFollowupClick(followups.q2!)}
            >
              {followups.q2}
            </button>
          )}
          {followups.q3 && (
            <button
              type="button"
              className={styles.followupChip}
              onClick={() => onFollowupClick(followups.q3!)}
            >
              {followups.q3}
            </button>
          )}
        </div>

        <div className={styles.inputAreaInner}>
          <div className={styles.plusWrap} ref={plusMenuRef}>
            <button
              type="button"
              className={styles.plusIconButton}
              onClick={() => setIsPlusMenuOpen(!isPlusMenuOpen)}
            >
              <PlusIcon className={styles.plusIcon} />
            </button>
            {isPlusMenuOpen && (
              <div className={styles.plusMenu}>
                <button type="button" onClick={() => setIsPlusMenuOpen(false)}>
                  写真・ファイルを追加
                  <ImageIcon width="16" height="16" />
                </button>
                <button type="button" onClick={() => setIsPlusMenuOpen(false)}>
                  参照データを指定
                  <DatabaseIcon width="16" height="16" />
                </button>
                <button
                  type="button"
                  className={isSuggestionMode ? styles.activePlusMenuItem : ''}
                  onClick={() => {
                    onSuggestionModeChange(true);
                    setIsPlusMenuOpen(false);
                  }}
                >
                  <span className={styles.plusMenuLabel}>AI提案の生成</span>
                  <IdeaIcon className={styles.ideaIcon} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          <div className={styles.chatInputWrap}>
            <textarea
              ref={textareaRef}
              className={styles.chatInput}
              value={input}
              placeholder={
                isFirstView
                  ? '気になることを聞いてください (例: CPAを改善したい)'
                  : 'ここにメッセージを入力'
              }
              onInput={handleInputResize}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {isSuggestionMode && (
              <div className={styles.modeArea}>
                <span className={styles.modeLabel}>
                  <IdeaIcon className={styles.ideaIcon} aria-hidden="true" />
                  AI提案
                  <button
                    type="button"
                    className={styles.modeCloseButton}
                    onClick={() => onSuggestionModeChange(false)}
                    aria-label="AI提案モードを解除"
                  >
                    ×
                  </button>
                </span>
              </div>
            )}
          </div>

          <button className={styles.sendButton} onClick={handleSend} disabled={isGenerating}>
            <ArrowWhiteIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
